/**
 * 安全的 localStorage 包装器
 * 解决服务端渲染和隐私模式下 localStorage 不可用的问题
 */

/**
 * 检查 localStorage 是否可用
 * 主要解决 "Access is denied for this document" 错误
 */
export const checkLocalStorageAvailable = (): boolean => {
  try {
    // 尝试访问 localStorage 属性本身
    // 在隐私模式或某些安全策略下，仅仅访问这个属性就会抛出错误
    // 如果能访问到这个属性，就认为 localStorage 基本可用
    const localStorage = window.localStorage;
    return !!localStorage;
  } catch {
    // 捕获属性访问错误：
    // - "Access is denied for this document"
    // - 其他安全限制错误
    return false;
  }
};

/**
 * 安全的 localStorage 包装器
 *
 * 性能优化策略：
 * - 首次调用时检查 localStorage 可用性并缓存结果
 * - 后续调用直接使用缓存结果（避免重复检查）
 * - 当 localStorage 操作失败时，重置缓存并在下次调用时重新检查
 * - 这样既保证了性能，又能适应环境变化（SSR -> 客户端，隐私模式切换等）
 */
class SafeStorage {
  private _isAvailable: boolean | null = null; // null 表示未初始化，true/false 表示已缓存的检查结果

  /**
   * 检查 localStorage 当前是否可用（带缓存优化）
   * @private
   */
  private checkAvailability(): boolean {
    // 如果已经检查过且可用，直接返回 true（避免重复检查）
    if (this._isAvailable === true) {
      return true;
    }

    // 重新检查可用性
    const isAvailable = checkLocalStorageAvailable();
    this._isAvailable = isAvailable;
    return isAvailable;
  }

  /**
   * 重置可用性缓存（当操作失败时调用）
   * @private
   */
  private resetAvailabilityCache(): void {
    this._isAvailable = null;
  }

  /**
   * 设置存储项
   * @param key 键名
   * @param value 值
   * @returns 是否设置成功
   */
  setItem(key: string, value: string): boolean {
    if (!this.available) {
      return false;
    }

    try {
      localStorage.setItem(key, value);
      return true;
    } catch {
      // 静默处理错误，操作失败时重置缓存
      this.resetAvailabilityCache();
      return false;
    }
  }

  /**
   * 获取存储项
   * @param key 键名
   * @returns 存储的值或 null（与原生 localStorage.getItem 行为一致）
   */
  getItem(key: string): string | null {
    if (!this.available) {
      return null;
    }

    try {
      return localStorage.getItem(key);
    } catch {
      // 静默处理错误，操作失败时重置缓存
      this.resetAvailabilityCache();
      return null;
    }
  }

  /**
   * 移除存储项
   * @param key 键名
   * @returns 是否移除成功
   */
  removeItem(key: string): boolean {
    if (!this.available) {
      return false;
    }

    try {
      localStorage.removeItem(key);
      return true;
    } catch {
      // 静默处理错误，操作失败时重置缓存
      this.resetAvailabilityCache();
      return false;
    }
  }

  /**
   * 清空所有存储项
   * @returns 是否清空成功
   */
  clear(): boolean {
    if (!this.available) {
      return false;
    }

    try {
      localStorage.clear();
      return true;
    } catch {
      // 静默处理错误，操作失败时重置缓存
      this.resetAvailabilityCache();
      return false;
    }
  }

  /**
   * 获取存储项数量
   * @returns 存储项数量
   */
  get length(): number {
    if (!this.available) {
      return 0;
    }

    try {
      return localStorage.length;
    } catch {
      // 静默处理错误，操作失败时重置缓存
      this.resetAvailabilityCache();
      return 0;
    }
  }

  /**
   * 根据索引获取键名
   * @param index 索引
   * @returns 键名或 null
   */
  key(index: number): string | null {
    if (!this.available) {
      return null;
    }

    try {
      return localStorage.key(index);
    } catch {
      // 静默处理错误，操作失败时重置缓存
      this.resetAvailabilityCache();
      return null;
    }
  }

  /**
   * 检查 localStorage 是否可用（实时检查）
   */
  get available(): boolean {
    return this.checkAvailability();
  }

  /**
   * 重新检查 localStorage 可用性（手动重置缓存并检查）
   * 用于强制重新检测 localStorage 状态
   */
  recheck(): boolean {
    this.resetAvailabilityCache();
    return this.checkAvailability();
  }
}

// 导出单例实例
export const safeStorage = new SafeStorage();
